/**
 * Collection Shuffle (Horizon)
 * ---------------------------------------------------------------------------
 * Shuffles the order of EVERY product in a collection once per visitor session,
 * then shows page N of that shuffled order when the shopper is on ?page=N.
 * Because the order is fixed for the session (seeded), paging 1 → 2 → 3 never
 * repeats a product, and the next visit gets a brand-new order.
 *
 * Product data comes from templates/collection.shuffle-data.liquid (250 per request,
 * cached in localStorage for 20 min). Cards are cloned from real cards the theme
 * rendered on the page, so they keep Horizon's look, image carousel and quick add.
 * If anything unexpected happens the native, unshuffled grid is left in place.
 *
 * Updated for the newer Horizon card markup: single-image cards have no slideshow,
 * prices render only the active branch, and badges are rendered only when needed.
 */
(function () {
  'use strict';

  var cfgEl = document.getElementById('CollectionShuffleConfig');
  if (!cfgEl || window.__collectionShuffleLoaded) return;
  window.__collectionShuffleLoaded = true;

  var cfg;
  try {
    cfg = JSON.parse(cfgEl.textContent);
  } catch (e) {
    reveal();
    return;
  }

  var VERSION = 1;
  var FEED_PAGE_SIZE = 250; // must match templates/collection.shuffle-data.liquid
  var DATA_TTL = 20 * 60 * 1000; // product data cache lifetime
  var SESSION_IDLE = 30 * 60 * 1000; // new shuffle after 30 min of inactivity
  var IMG_WIDTHS = [240, 352, 640, 832, 1200];

  var html = document.documentElement;
  var applying = false;

  function reveal() {
    html.classList.remove('cshuffle-pending');
  }

  function warn(msg, err) {
    if (window.console) console.warn('[collection-shuffle] ' + msg, err || '');
  }

  /* ---------------------------------------------------------------- helpers */

  function isEligible() {
    var params = new URLSearchParams(location.search);
    var ok = true;
    params.forEach(function (_v, k) {
      if (k === 'sort_by' || k === 'q' || k.indexOf('filter.') === 0) ok = false;
    });
    if (/bot|crawl|spider|slurp/i.test(navigator.userAgent)) ok = false;
    return ok;
  }

  function currentPage() {
    var p = parseInt(new URLSearchParams(location.search).get('page'), 10);
    return p > 0 ? p : 1;
  }

  function store(type) {
    try {
      var s = window[type];
      var k = '__cshuffle_test';
      s.setItem(k, '1');
      s.removeItem(k);
      return s;
    } catch (e) {
      return null;
    }
  }
  var ss = store('sessionStorage');
  var ls = store('localStorage');

  function random32() {
    try {
      return crypto.getRandomValues(new Uint32Array(1))[0];
    } catch (e) {
      return Math.floor(Math.random() * 4294967296);
    }
  }

  function hashString(str) {
    var h = 2166136261;
    for (var i = 0; i < str.length; i++) {
      h ^= str.charCodeAt(i);
      h = Math.imul(h, 16777619);
    }
    return h >>> 0;
  }

  function mulberry32(a) {
    return function () {
      a |= 0;
      a = (a + 0x6d2b79f5) | 0;
      var t = Math.imul(a ^ (a >>> 15), 1 | a);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  /** One seed per browsing session; refreshed after SESSION_IDLE of inactivity. */
  function sessionSeed() {
    var key = 'cshuffle:seed';
    var now = Date.now();
    var obj = null;
    if (ss) {
      try {
        obj = JSON.parse(ss.getItem(key));
      } catch (e) {}
    }
    if (!obj || typeof obj.seed !== 'number' || now - obj.ts > SESSION_IDLE) {
      // Without sessionStorage fall back to a daily seed so paging stays consistent.
      obj = { seed: ss ? random32() : hashString(new Date().toDateString()), ts: now };
    }
    obj.ts = now;
    if (ss) {
      try {
        ss.setItem(key, JSON.stringify(obj));
      } catch (e) {}
    }
    return obj.seed;
  }

  /** Deterministic Fisher–Yates shuffle (same seed + collection = same order). */
  function shuffle(items, seed) {
    var arr = items.slice().sort(function (a, b) {
      return a.id - b.id;
    });
    var rand = mulberry32((seed ^ hashString(cfg.handle)) >>> 0);
    for (var i = arr.length - 1; i > 0; i--) {
      var j = Math.floor(rand() * (i + 1));
      var tmp = arr[i];
      arr[i] = arr[j];
      arr[j] = tmp;
    }
    return arr;
  }

  /* ------------------------------------------------------------ data feed */

  function cacheKey() {
    return ['cshuffle', 'v' + VERSION, cfg.handle, cfg.currency || '', cfg.locale || ''].join(':');
  }

  function readCache() {
    if (!ls) return null;
    try {
      var obj = JSON.parse(ls.getItem(cacheKey()));
      if (obj && Date.now() - obj.ts < DATA_TTL && obj.count === cfg.count && obj.items) return obj.items;
    } catch (e) {}
    return null;
  }

  function writeCache(items) {
    if (!ls) return;
    var payload = JSON.stringify({ ts: Date.now(), count: cfg.count, items: items });
    try {
      ls.setItem(cacheKey(), payload);
    } catch (e) {
      // Quota hit: drop other cached collections and try once more.
      try {
        for (var i = ls.length - 1; i >= 0; i--) {
          var k = ls.key(i);
          if (k && k.indexOf('cshuffle:') === 0) ls.removeItem(k);
        }
        ls.setItem(cacheKey(), payload);
      } catch (e2) {}
    }
  }

  function feedUrl(page) {
    var u = new URL(cfg.url, location.origin);
    u.searchParams.set('view', 'shuffle-data');
    u.searchParams.set('page', String(page));
    var preview = new URLSearchParams(location.search).get('preview_theme_id');
    if (preview) u.searchParams.set('preview_theme_id', preview);
    return u.toString();
  }

  function fetchFeedPage(page) {
    return fetch(feedUrl(page), { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      })
      .then(function (text) {
        return JSON.parse(text.slice(text.indexOf('{')));
      });
  }

  function range(from, to) {
    var out = [];
    for (var i = from; i <= to; i++) out.push(i);
    return out;
  }

  function fetchAll() {
    var pages = Math.max(1, Math.ceil(cfg.count / FEED_PAGE_SIZE));
    return Promise.all(range(1, pages).map(fetchFeedPage)).then(function (results) {
      var reported = results[0] && results[0].pages ? results[0].pages : pages;
      var more = reported > pages ? Promise.all(range(pages + 1, reported).map(fetchFeedPage)) : Promise.resolve([]);
      return more.then(function (extra) {
        var seen = {};
        var items = [];
        results.concat(extra).forEach(function (res) {
          (res.products || []).forEach(function (p) {
            if (!seen[p.id]) {
              seen[p.id] = true;
              items.push(p);
            }
          });
        });
        return items;
      });
    });
  }

  var dataPromise = null;
  function loadProducts() {
    if (!dataPromise) {
      var cached = readCache();
      dataPromise = cached
        ? Promise.resolve(cached)
        : fetchAll().then(function (items) {
            writeCache(items);
            return items;
          });
      dataPromise.catch(function () {
        dataPromise = null;
      });
    }
    return dataPromise;
  }

  /* ---------------------------------------------------------- card builder */

  function imgUrl(path, width) {
    var base = /^(https?:)?\/\//.test(path) ? path : '/cdn/shop/' + path;
    return width ? base + (base.indexOf('?') > -1 ? '&' : '?') + 'width=' + width : base;
  }

  function pathOf(href) {
    try {
      return new URL(href, location.origin).pathname;
    } catch (e) {
      return href;
    }
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /** Swap every reference to the template product (ids, urls, title) for the new product. */
  function rebind(root, from, to) {
    var fromId = String(from.id);
    var toId = String(to.id);
    var fromVid = from.vid ? String(from.vid) : null;
    var toVid = to.vid ? String(to.vid) : '';
    var fromPath = pathOf(from.u);
    var fromTitle = String(from.t).trim();

    var els = [root].concat(Array.prototype.slice.call(root.querySelectorAll('*')));
    els.forEach(function (el) {
      Array.prototype.slice.call(el.attributes).forEach(function (attr) {
        var v = attr.value;
        var nv = v;
        if (attr.name === 'view-event-payload') {
          try {
            var payload = JSON.parse(v);
            if (payload && payload.product) {
              payload.product.id = to.id;
              payload.product.title = to.t;
              payload.product.handle = to.h;
              payload.product.selectedVariant = null;
              nv = JSON.stringify(payload);
            }
          } catch (e) {}
        } else if (attr.name === 'href' || attr.name === 'data-product-url') {
          if (pathOf(v) === fromPath) nv = to.u;
        } else if (v.trim() === fromTitle) {
          nv = to.t;
        } else {
          if (nv.indexOf(fromId) > -1) nv = nv.split(fromId).join(toId);
          if (fromVid && nv.indexOf(fromVid) > -1) nv = nv.split(fromVid).join(toVid);
        }
        if (nv !== v) el.setAttribute(attr.name, nv);
      });
    });

    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
    var node;
    while ((node = walker.nextNode())) {
      if (node.nodeValue.trim() === fromTitle) node.nodeValue = node.nodeValue.replace(fromTitle, to.t);
    }
  }

  /** Point one .product-media-container (and its image) at a product photo. */
  function fillMedia(container, media, i, product, eager) {
    var pm = container.querySelector('.product-media');
    if (pm) {
      pm.style.setProperty('--ratio', String(media[1]));
      pm.setAttribute('data-media-id', product.id + '-' + i);
    }
    var img = container.querySelector('img');
    if (img) {
      var ratio = media[1] || 1;
      img.setAttribute('loading', i === 0 && eager ? 'eager' : 'lazy');
      if (!(i === 0 && eager)) img.setAttribute('fetchpriority', 'auto');
      img.setAttribute('width', '1200');
      img.setAttribute('height', String(Math.round(1200 / ratio)));
      img.setAttribute('alt', product.t);
      img.removeAttribute('style');
      img.setAttribute('data_max_resolution', imgUrl(media[0], 1200));
      img.setAttribute('data-max-resolution', imgUrl(media[0], 1200));
      img.setAttribute(
        'srcset',
        IMG_WIDTHS.map(function (w) {
          return imgUrl(media[0], w) + ' ' + w + 'w';
        }).join(', ')
      );
      img.setAttribute('src', imgUrl(media[0], 1200));
    }
  }

  function buildSlide(tplSlide, media, i, product, eager) {
    var slide = tplSlide.cloneNode(true);
    slide.removeAttribute('hidden');
    slide.removeAttribute('variant-image');
    slide.setAttribute('aria-hidden', i === 0 ? 'false' : 'true');
    slide.setAttribute('slide-id', product.id + '-' + i);
    var style = slide.getAttribute('style');
    if (style) slide.setAttribute('style', style.replace(/--slide-\d+/, '--slide-' + i));
    fillMedia(slide, media, i, product, eager);
    return slide;
  }

  /** Product without photos: show the title in the image area, like Horizon does. */
  function showTitlePlaceholder(gallery, product) {
    var link = gallery.querySelector('a.contents');
    if (!link) return;
    var ph = document.createElement('div');
    ph.className = 'product-card-gallery__title-placeholder';
    var a = document.createElement('a');
    a.className = 'contents';
    a.setAttribute('ref', 'cardGalleryLink');
    a.setAttribute('href', product.u);
    a.innerHTML = '<span class="title-text">' + esc(product.t) + '</span>';
    ph.appendChild(a);
    link.replaceWith(ph);
  }

  function rebuildGallery(card, product, arrowsTpl, eager) {
    var gallery = card.querySelector('.card-gallery');
    if (!gallery) return;
    var media = product.m || [];

    if (gallery.getAttribute('data-image-ratio') === 'adapt') {
      gallery.style.setProperty('--gallery-aspect-ratio', String(media[0] ? media[0][1] : 1));
    }

    if (!media.length) {
      showTitlePlaceholder(gallery, product);
      return;
    }

    var slideshow = gallery.querySelector('slideshow-component');
    var slidesWrap = slideshow && slideshow.querySelector('slideshow-slides');
    var tplSlide = slidesWrap && slidesWrap.querySelector('slideshow-slide');

    // Single-photo template card (newer Horizon renders no slideshow for these).
    if (!slideshow || !slidesWrap || !tplSlide) {
      var single = gallery.querySelector('.product-media-container');
      if (single) fillMedia(single, media[0], 0, product, eager);
      return;
    }

    var container = slideshow.querySelector('slideshow-container');
    var arrows = container && container.querySelector('slideshow-arrows');

    var slides = media.map(function (m, i) {
      return buildSlide(tplSlide, m, i, product, eager);
    });
    slidesWrap.innerHTML = '';
    slides.forEach(function (s) {
      slidesWrap.appendChild(s);
    });

    slideshow.style.setProperty(
      '--slideshow-timeline',
      media
        .map(function (_m, i) {
          return '--slide-' + i;
        })
        .join(', ')
    );
    slideshow.setAttribute('initial-slide', '0');
    slideshow.setAttribute('data-generic-media-size', String(media.length));

    // Horizon shows arrows on cards with more than 2 images.
    if (media.length > 2) {
      if (!arrows && arrowsTpl && container) container.insertBefore(arrowsTpl.cloneNode(true), slidesWrap);
    } else if (arrows) {
      arrows.remove();
    }
  }

  /** Horizon only renders a badge container for sold-out / on-sale products. */
  function updateBadges(card, product) {
    var gallery = card.querySelector('.card-gallery');
    if (!gallery) return;
    var b = cfg.badges || {};

    var existing = gallery.querySelector(':scope > .product-badges');
    if (existing) existing.remove();
    Array.prototype.slice.call(gallery.classList).forEach(function (c) {
      if (c.indexOf('card-gallery--badge-') === 0) gallery.classList.remove(c);
    });

    var label = null;
    var cls = null;
    if (!product.a) {
      label = b.soldOut;
      cls = 'color-custom-badge-sold-out';
    } else if (product.c) {
      label = b.sale;
      cls = 'color-custom-badge-sale';
    }
    if (!label) return;

    var pos = b.position || 'top-left';
    gallery.classList.add('card-gallery--badge-' + pos);

    var wrap = document.createElement('div');
    wrap.className = 'product-badges product-badges--' + pos;
    var fam = b.fontFamily || 'body';
    wrap.setAttribute(
      'style',
      '--badge-border-radius: ' + (b.radius || 0) + 'px; --badge-font-family: var(--font-' + fam +
        '--family); --badge-font-weight: var(--font-' + fam + '--weight); --badge-text-transform: ' +
        (b.textTransform || 'none') + ';'
    );
    wrap.innerHTML =
      '<div class="product-badges__badge product-badges__badge--rectangle ' + cls + '">' + esc(label) + '</div>';
    var qa = gallery.querySelector(':scope > quick-add-component');
    gallery.insertBefore(wrap, qa || null);
  }

  /** Newer Horizon renders only the active price branch (regular OR sale + compare-at). */
  function updatePrice(card, product, saleFirst) {
    var pc = card.querySelector('product-price [ref="priceContainer"]') || card.querySelector('[ref="priceContainer"]');
    if (!pc) return;
    var l = cfg.labels || {};
    if (product.c) {
      var sale =
        '<span class="price-item__group price"><span class="visually-hidden">' + esc(l.sale) + '&nbsp;</span><bdi>' +
        esc(product.p) + '</bdi></span>';
      var regular =
        '<span class="price-item__group price-item--regular compare-at-price"><span class="visually-hidden">' +
        esc(l.regular) + '&nbsp;</span><bdi>' + esc(product.c) + '</bdi></span>';
      pc.innerHTML = saleFirst ? sale + regular : regular + sale;
    } else {
      pc.innerHTML = '<span class="price"><bdi>' + esc(product.p) + '</bdi></span>';
    }
  }

  function updateQuickAdd(card, product) {
    var content = card.querySelector('.product-card__content');
    var qa = !!cfg.quickAdd && product.a;
    var mqa = qa && !!cfg.mobileQuickAdd;
    if (content) {
      content.style.setProperty('--quick-add-display', qa ? 'flex' : 'none');
      content.style.setProperty('--quick-add-mobile-display', mqa ? 'flex' : 'none');
      content.style.setProperty('--quick-add-mobile-opacity', mqa ? '1' : '0');
    }
    var comp = card.querySelector('quick-add-component');
    if (!comp) return;
    comp.setAttribute('data-product-title', product.t);
    comp.setAttribute('data-quick-add-button', product.vc === 1 ? 'add' : 'choose');
    comp.setAttribute('data-product-options-count', String(product.oc || 1));
    var idInput = comp.querySelector('input[name="id"]');
    if (idInput) {
      idInput.setAttribute('value', String(product.vid));
      idInput.value = String(product.vid);
      if (product.a) idInput.removeAttribute('disabled');
      else idInput.setAttribute('disabled', '');
    }
    var addBtn = comp.querySelector('.quick-add__button--add');
    if (addBtn) {
      if (product.a) addBtn.removeAttribute('disabled');
      else addBtn.setAttribute('disabled', '');
    }
    var atc = comp.querySelector('add-to-cart-component');
    if (atc && product.m && product.m[0]) {
      atc.setAttribute('data-product-variant-media', imgUrl(product.m[0][0], 100));
    }
  }

  function buildCard(tpl, product, index, page, arrowsTpl, saleFirst) {
    var li = tpl.li.cloneNode(true);
    rebind(li, tpl.product, product);

    li.id = cfg.sectionId + '-' + product.id;
    li.setAttribute('data-product-id', String(product.id));
    li.setAttribute('data-page', String(page));
    li.setAttribute('data-cshuffle', '1');
    li.className = li.className.replace(/product-grid__item--\d+/, 'product-grid__item--' + index);

    var cardEl = li.querySelector('product-card');
    if (cardEl && product.m && product.m[0]) {
      cardEl.setAttribute('data-featured-media-url', imgUrl(product.m[0][0], 400));
    }
    var link = li.querySelector('.product-card__link');
    if (link) link.id = 'cshuffle-link-' + product.id;

    rebuildGallery(li, product, arrowsTpl, index < 8);
    updateBadges(li, product);
    updatePrice(li, product, saleFirst);
    updateQuickAdd(li, product);
    return li;
  }

  /* --------------------------------------------------------------- render */

  function getGrid() {
    return document.querySelector('.main-collection-grid ul.product-grid') || document.querySelector('ul.product-grid');
  }

  function gridItems(grid) {
    return Array.prototype.filter.call(grid.children, function (el) {
      return el.matches('li.product-grid__item');
    });
  }

  function scrollToHash() {
    if (!location.hash) return;
    var el = document.getElementById(location.hash.slice(1));
    if (el) el.scrollIntoView({ behavior: 'instant', block: 'start' });
  }

  /** Small collections (one page): just reorder the cards already on the page. */
  function shuffleInPlace(grid) {
    var lis = gridItems(grid);
    var fake = lis.map(function (li, i) {
      return { id: parseInt(li.getAttribute('data-product-id'), 10) || i, li: li };
    });
    shuffle(fake, sessionSeed()).forEach(function (o, i) {
      o.li.className = o.li.className.replace(/product-grid__item--\d+/, 'product-grid__item--' + i);
      o.li.setAttribute('data-cshuffle', '1');
      grid.appendChild(o.li);
    });
  }

  function render(items) {
    var grid = getGrid();
    if (!grid) return false;
    var lis = gridItems(grid);
    if (!lis.length) return false;

    var byId = {};
    items.forEach(function (p) {
      byId[p.id] = p;
    });

    // Templates = real cards rendered by the theme whose product we have data for.
    // 'multi' has an image slideshow, 'single' is a plain one-photo card.
    var tpls = { single: null, multi: null };
    for (var i = 0; i < lis.length; i++) {
      var pid = lis[i].getAttribute('data-product-id');
      if (!byId[pid] || !lis[i].querySelector('product-card')) continue;
      var kind = lis[i].querySelector('.card-gallery slideshow-component') ? 'multi' : 'single';
      if (!tpls[kind]) tpls[kind] = { li: lis[i], product: byId[pid] };
      if (tpls.single && tpls.multi) break;
    }
    if (!tpls.single && !tpls.multi) throw new Error('no template card');

    var arrowsSrc = grid.querySelector('.card-gallery slideshow-arrows');
    var arrowsTpl = arrowsSrc ? arrowsSrc.cloneNode(true) : null;

    // Sale price order: Horizon's price block can show sale-first or regular-first.
    var saleFirst = true;
    var cmp = grid.querySelector('product-price .compare-at-price');
    if (cmp && !cmp.previousElementSibling) saleFirst = false;

    var page = currentPage();
    var per = cfg.perPage || 36;
    var slice = shuffle(items, sessionSeed()).slice((page - 1) * per, page * per);
    if (!slice.length) return false;

    var frag = document.createDocumentFragment();
    slice.forEach(function (p, idx) {
      var n = (p.m || []).length;
      var tpl = n > 1 && tpls.multi ? tpls.multi : tpls.single || tpls.multi;
      frag.appendChild(buildCard(tpl, p, idx, page, arrowsTpl, saleFirst));
    });

    grid.insertBefore(frag, lis[0]);
    lis.forEach(function (li) {
      li.remove();
    });
    return true;
  }

  function run() {
    if (!isEligible()) {
      reveal();
      return;
    }
    var grid = getGrid();
    if (!grid || !gridItems(grid).length) {
      reveal();
      return;
    }

    if (cfg.count <= (cfg.perPage || 36)) {
      applying = true;
      try {
        shuffleInPlace(grid);
      } catch (e) {
        warn('in-place shuffle failed', e);
      }
      applying = false;
      reveal();
      scrollToHash();
      return;
    }

    loadProducts()
      .then(function (items) {
        applying = true;
        try {
          render(items);
        } finally {
          applying = false;
        }
        scrollToHash();
      })
      .catch(function (e) {
        warn('left native order in place', e);
      })
      .then(reveal);
  }

  /** Re-apply after Horizon re-renders the grid (e.g. shopper clears all filters). */
  function watch() {
    var root = document.querySelector('results-list') || (getGrid() && getGrid().parentElement);
    if (!root || !window.MutationObserver) return;
    var timer = null;
    new MutationObserver(function () {
      if (applying) return;
      clearTimeout(timer);
      timer = setTimeout(function () {
        if (applying || !isEligible()) return;
        var grid = getGrid();
        if (grid && grid.querySelector(':scope > li.product-grid__item:not([data-cshuffle])')) run();
      }, 80);
    }).observe(root, { childList: true, subtree: true });
  }

  // Start downloading product data immediately, render once the grid is parsed.
  if (isEligible() && cfg.count > (cfg.perPage || 36)) {
    loadProducts().catch(function () {});
  }

  function start() {
    run();
    watch();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
